// Centralized API client. Injects Clerk session token into every request.
// Returns typed results; surfaces server errors as thrown Error with context.

const API_BASE = '/api';

type FetcherInit = Omit<RequestInit, 'body'> & { body?: unknown; rawBody?: boolean };

async function getToken(): Promise<string | null> {
  const clerk = (window as unknown as { Clerk?: { session?: { getToken(): Promise<string> } } }).Clerk;
  if (!clerk?.session) return null;
  return clerk.session.getToken();
}

export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly payload: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T>(path: string, init: FetcherInit = {}): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  // Dev-only: when the Vite build injects a preview bypass secret, every
  // request carries it so the server's matching middleware short-circuits
  // Clerk. The secret is ONLY set via `.env.local` for local preview runs —
  // production builds never see it and the server also double-gates on
  // NODE_ENV, so a stray header in prod is a no-op.
  const previewSecret = import.meta.env.VITE_PREVIEW_BYPASS_SECRET as string | undefined;
  if (previewSecret) {
    headers.set('X-Preview-Bypass', previewSecret);
  } else {
    const token = await getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  let body: BodyInit | undefined;
  if (init.body !== undefined) {
    if (init.rawBody) {
      body = init.body as BodyInit;
    } else {
      headers.set('Content-Type', 'application/json');
      body = JSON.stringify(init.body);
    }
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers, body });

  if (!res.ok) {
    let payload: unknown = null;
    try {
      payload = await res.json();
    } catch {
      try {
        payload = await res.text();
      } catch {
        /* swallow */
      }
    }
    const message = `API ${init.method ?? 'GET'} ${path} → ${res.status}`;
    throw new ApiError(message, res.status, payload);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// Convenience verbs.
export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
